import { toRef, ref, watch } from "vue";
import set from "@/functions/set";
import type { Ref } from "vue";

/**
 * @function useProp - composable function that gives you a value from a prop that can be modifiable without triggering vue
 * @param {Prop extends object} props - reactive object that contains the props, returned from defineProps function
 * @param {PropName extends keyof Props} prop - the name of the prop
 * @param {PropType} defaultValue - the value in case the prop has no default and is needed to not be undefined
 * @returns {Ref<PropType>} the modifiable ref value
 */
export default function useProp<
  PropType,
  Props extends object,
  PropName extends keyof Props
>(props: Props, prop: PropName, defaultValue: PropType): Ref<PropType> {
  //obtain  ref value from the props
  const temporaryValue = toRef(
    props,
    prop,
    defaultValue as unknown as Props[PropName]
  ) as Ref<PropType>;

  //create a new value that depends on the temp value that also depends on the prop
  const value = ref(temporaryValue.value) as Ref<PropType>;

  //if the prop changes, modify the value accordingly
  watch(temporaryValue, (newValue) => set(value, newValue));

  return value;
}
